// Copyright (c) Docugami, Inc. All rights reserved.

import { ICommand, ICommandButton } from "components/cards/CommandButton";
import { ISlotItem, SlotItemType, ChunkSlotItem, IItemSlotMarker } from "components/cards/ItemSlot";
import { greyText, primaryTheme, themeBlue, themeWhite } from "components/shell/presentation/AppStyles";
import { ChunkInstance } from "networking/api";
import { useMemo } from "react";
import { RequestState, useAsyncHelper } from "./AsyncHelper";
import { IconGenerator, dgIconNames } from "./dgIcons";
import { useDiligenceFlaggingEnabled } from "components/work_path/WorkPathsHelper";
import { ToggleCommand, ToggleType } from "components/reports/ToggleCommandBar";

export function useSlotForChunk(spanId?: string): ISlotItem | null {
    return useMemo<ISlotItem | null>(() => {
        if (!spanId) {
            return null;
        }

        const item: ChunkSlotItem = {
            id: spanId,
            type: SlotItemType.Chunk,
            spanId: spanId,
        };

        return item;
    }, [spanId]);
}

export interface SlotToggleCommandProps {
    flag?: ICommand;
    isFlagged?: boolean;
}

export function useSlotToggleCommands(props: SlotToggleCommandProps): ToggleCommand[] | undefined {
    const { flag, isFlagged } = props;
    const isFlaggingEnabled = useDiligenceFlaggingEnabled();

    if (isFlaggingEnabled && !!flag) {
        return [{
            command: flag,
            isToggled: !!isFlagged,
            type: ToggleType.Flag
        }]
    }
}

export interface SlotCommandButtonsProps {
    confirm?: ICommand;
    isConfirmed?: boolean;
    clear?: ICommand;
    onExpand?: () => void;
    edit?: ICommand;
    example?: ICommand;
    isExample?: boolean;
    showLabel?: boolean;
}

export function useSlotCommandButtons(props: SlotCommandButtonsProps): ICommandButton[] {
    const { execute: executeConfirm, state: confirmRequestState } = useAsyncHelper("Confirm instance");
    const { execute: executeClear, state: clearRequestState } = useAsyncHelper("Clear instance");
    const { execute: executeEdit, state: editRequestState } = useAsyncHelper("Edit instance");

    const { confirm, clear, onExpand, edit, isConfirmed, example, isExample } = props;

    const confirmCommand = useMemo<ICommandButton | null>(() => {
        if (!confirm || confirm.hidden) {
            return null;
        }

        async function confirmAsync() {
            await confirm?.execute();
        }

        const command: ICommandButton = {
            icon: {
                name: dgIconNames.DgConfirmIcon,
                width: 1,
                height: 1,
                defaultColor: !!isConfirmed ? primaryTheme.palette.white : greyText,
                backgroundColor: !!isConfirmed ? primaryTheme.palette.themePrimary : undefined
            },
            showLabel: props.showLabel,
            label: 'Confirm',
            isOverflow: false,
            canExecute: confirmRequestState !== RequestState.InProgress && confirm.canExecute(),
            onClick: () => executeConfirm(confirmAsync())
        };

        return command;
    }, [confirm, confirmRequestState, executeConfirm, isConfirmed, props.showLabel]);

    const clearCommand = useMemo<ICommandButton | null>(() => {
        if (!clear || clear.hidden) {
            return null;
        }

        async function clearAsync() {
            await clear!.execute();
        }

        const command: ICommandButton = {
            icon: {
                name: dgIconNames.DgClearIcon,
                width: 1,
                height: 1,
                defaultColor: primaryTheme.palette.neutralSecondary
            },
            showLabel: props.showLabel,
            label: 'Clear content',
            isOverflow: clear.primary !== true,
            canExecute: clearRequestState !== RequestState.InProgress && clear!.canExecute(),
            onClick: () => executeClear(clearAsync())
        };

        return command;
    }, [clear, clearRequestState, executeClear, props.showLabel]);

    const expandCommand = useMemo<ICommandButton | null>(() => {
        if (!onExpand) {
            return null;
        }

        const command: ICommandButton = {
            icon: {
                name: dgIconNames.DgExpandIcon,
                width: 1,
                height: 1,
                defaultColor: primaryTheme.palette.neutralSecondary
            },
            showLabel: props.showLabel,
            label: 'Expand content',
            canExecute: true,
            isOverflow: false,
            onClick: onExpand,
        };

        return command;
    }, [onExpand, props.showLabel]);

    const editCommand = useMemo<ICommandButton | null>(() => {
        if (!edit || edit.hidden) {
            return null;
        }

        async function editAsync() {
            await edit!.execute();
        }

        const command: ICommandButton = {
            icon: {
                name: dgIconNames.TextEdit,
                width: 1.4,
                height: 1,
                defaultColor: primaryTheme.palette.neutralSecondary
            },
            showLabel: props.showLabel,
            label: 'Edit content',
            isOverflow: true,
            canExecute: editRequestState !== RequestState.InProgress && edit!.canExecute(),
            onClick: () => executeEdit(editAsync())
        };

        return command;
    }, [edit, editRequestState, executeEdit, props.showLabel]);

    const toggleExampleCommand = useMemo<ICommandButton | null>(() => {
        if (!example || example.hidden) {
            return null;
        }

        const commandButton: ICommandButton = {
            icon: {
                name: dgIconNames.DgExampleChunk,
                width: 1,
                height: .7,
                defaultColor: !!isExample ? primaryTheme.palette.neutralSecondary : themeBlue
            },
            showLabel: props.showLabel,
            label: !!isExample ? "Remove Example" : "Add Example",
            isOverflow: true,
            canExecute: example.canExecute(),
            onClick: example.execute
        }

        return commandButton;
    }, [example, isExample, props.showLabel])

    return useMemo(() => {
        const commands: ICommandButton[] = [];

        if (!!confirmCommand) {
            commands.push(confirmCommand);
        }

        if (!!clearCommand) {
            commands.push(clearCommand);
        }

        if (!!expandCommand) {
            commands.push(expandCommand);
        }

        if (!!editCommand) {
            commands.push(editCommand);
        }

        if (!!toggleExampleCommand) {
            commands.push(toggleExampleCommand);
        }

        return commands;
    }, [confirmCommand, clearCommand, expandCommand, editCommand, toggleExampleCommand]);
}

export enum ChunkSlotFlags {
    Property,
    Edited,
    Example
}

export function getFlagsForChunk(chunkInstance: ChunkInstance | null, isExample?: boolean): ChunkSlotFlags[] {
    const flags: ChunkSlotFlags[] = [];

    if (chunkInstance?.addedChunkInfo) {
        flags.push(ChunkSlotFlags.Property);
    }

    if (isExample) {
        flags.push(ChunkSlotFlags.Example)
    }

    return flags;
}

export function mapChunkFlagToSlotMarker(flag: ChunkSlotFlags, icons: IconGenerator): IItemSlotMarker | null {
    switch (flag) {
        case ChunkSlotFlags.Property:
            return ({
                label: "Property Chunk",
                icon: "P",
                color: themeBlue,
                tooltip: "Property Chunk"
            })
        case ChunkSlotFlags.Example:
            return ({
                label: "Example Chunk",
                icon: icons(dgIconNames.DgExampleChunk, '', 8.4, 12, themeBlue, '', ''),
                color: themeWhite,
                tooltip: "Example Chunk"
            })
        default:
            return null;
    }
}
